import {
  Box,
  Flex,
  FormControl,
  HStack,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  StackDivider,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { TbChecks, TbAlertTriangle, TbCircleX, TbChevronDown } from "react-icons/tb";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import { get, startCase } from "lodash";
import generateSchedulingSummary from "features/Trigger/helpers";
import { PrimaryButton } from "components/Button";
import { dirtyValues } from "helpers/form";
import NameField from "features/Resources/Shared/Components/Fields/NameField";
import { Path } from "features/Path/models";
import TemplateResourceOptionMenu from "features/Resources/SharedTemplate/Components/TemplateResourceOptionMenu";
import { v2pathPathById } from "helpers/url/pages/v2/pathPath";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import TagByResourceType from "components/Tag/TagByResourceType";
import ArchivedTag from "components/Tag/ArchivedTag";
import BasePathTag from "components/Tag/BasePathTag";
import ErrorText from "components/FormElements/ErrorText";
import AttributeColumnLabel from "features/Resources/Shared/Components/AttributeColumnLabel";
import SelectPathsMenuButton from "features/Path/Components/Button/SelectPathsMenuButton";
import SchedulingTriggerModalButton from "features/Resources/Shared/Components/Button/SchedulingTriggerModalButton";
import { usePathsFieldArray } from "features/Resources/Shared/hooks/usePathsFieldArray";
import { AttributeButtonWithRef } from "components/Button/AttributeButton";
import UserOrChannelSelectField from "components/FormElements/Fields/SelectFields/UserOrChannelSelectField";
import UserRoleOrEmailMultiSelect, {
  UserRoleOrEmailOption,
} from "components/FormElements/Fields/SelectFields/UserRoleOrEmailMultiSelect";
import GenericSelectable from "components/Selectable/GenericSelectable";
import { CheckpointTemplate } from "features/Resources/CheckpointTemplate/models";
import { useCreateCheckpointTemplate, useUpdateCheckpointTemplate } from "features/Resources/CheckpointTemplate/hooks";
import { toCheckpointTemplateFormData } from "features/Resources/CheckpointTemplate/helpers";
import { checkpointTemplateSchema, CheckpointTemplateSchema } from "../schema";
import { Heading16 } from "design-system";
import { CHECK_TARGET_OPTIONS, DELIVERY_METHOD_OPTIONS } from "features/Resources/Shared/constants";

export default function CheckpointTemplateForm({ id, data }: { id: string; data: CheckpointTemplate }) {
  const toast = useToast();
  const navigate = useNavigate();
  const form = useForm<CheckpointTemplateSchema>({
    mode: "onChange",
    resolver: yupResolver(checkpointTemplateSchema),
    values: toCheckpointTemplateFormData(data),
    defaultValues: { paths: [], recipients: [], checkTarget: "new_hire_tasks", deliveryMethod: "message" },
  });
  const { mutate: onUpdate } = useUpdateCheckpointTemplate(data?.id);
  const { mutate: onCreate } = useCreateCheckpointTemplate();
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { dirtyFields },
  } = form;

  const checkTarget = watch("checkTarget");
  const deliveryMethod = watch("deliveryMethod");
  const evaluateAtTrigger = watch("evaluateAtTrigger");
  const [recipientNames, setRecipientNames] = useState<string[]>([]);
  const [hasChannelRecipient, setHasChannelRecipient] = useState<boolean>(false);

  // Sync recipient display names from template data
  useEffect(() => {
    if (!data?.recipients?.length) return;
    const names = data.recipients
      .map((r: any) => {
        const isRole = r?.targetType === "OnboardingJourneyNetworkRelation";
        const target = r?.target;
        if (!target) return r?.label || "";
        const name = target.fullName || target.name || "";
        return isRole ? `the ${startCase(name)}` : name;
      })
      .filter(Boolean);
    setRecipientNames(names);
    setHasChannelRecipient(data.recipients.some((r: any) => r?.targetType === "MessageServiceChannel"));
  }, [data]);

  const onMessageRecipientsChange = useCallback((rawValues: any) => {
    if (!rawValues) {
      setRecipientNames([]);
      setHasChannelRecipient(false);
      return;
    }
    const values = Array.isArray(rawValues) ? rawValues : [rawValues];
    setRecipientNames(
      values
        .map((v: any) => {
          const isRole = v?.recordType === "OnboardingJourneyNetworkRelation";
          const name = v?.name || "";
          return isRole ? `the ${startCase(name)}` : name;
        })
        .filter(Boolean),
    );
    setHasChannelRecipient(values.some((v: any) => v?.recordType === "MessageServiceChannel"));
  }, []);

  // Paths field logic
  const { pathFields, removePath, addPathId, selectedPathIds, getPathData, returnPathId } = usePathsFieldArray(form);

  const selectedPathsComponents = useMemo(
    () =>
      (pathFields || []).map((field: any, index: number) => {
        const path = getPathData(field.id);
        return (
          <BasePathTag
            key={field.fieldId}
            pathId={path?.id}
            pathName={path?.name}
            icon={path?.icon}
            iconColor={path?.iconColor}
            removeable
            onRemove={() => removePath(index)}
          />
        );
      }),
    [pathFields, getPathData, removePath],
  );

  // Check Target popover
  const { isOpen: isCheckTargetOpen, onOpen: onCheckTargetOpen, onClose: onCheckTargetClose } = useDisclosure();

  // Delivery Method popover
  const {
    isOpen: isDeliveryMethodOpen,
    onOpen: onDeliveryMethodOpen,
    onClose: onDeliveryMethodClose,
  } = useDisclosure();

  const onSubmit = (formData: Partial<CheckpointTemplateSchema>) => {
    const submitData = id === "new" ? formData : dirtyValues(formData, dirtyFields);

    if (id !== "new" && (!submitData || Object.keys(submitData).length === 0)) {
      return;
    }

    if (submitData.paths) {
      submitData.pathIds = ((submitData?.paths || []) as Path[]).map((path) => path.id);
    }

    // When delivery method changes, always include recipients
    if (submitData.deliveryMethod && !submitData.recipients) {
      submitData.recipients = formData.recipients;
    }

    if (id === "new") {
      onCreate(submitData as CheckpointTemplateSchema, {
        onSuccess: () => {
          toast({ title: "Checkpoint template created.", status: "success" });
          if (returnPathId) {
            navigate(v2pathPathById(returnPathId));
          } else if (formData?.pathIds?.length) {
            navigate(v2pathPathById(formData.pathIds[0]));
          } else {
            navigate(PEOPLE_TEAM_ROUTES.checkpointTemplates.index);
          }
        },
      });
    } else {
      onUpdate(submitData, { onSuccess: () => toast({ title: "Checkpoint template updated.", status: "success" }) });
    }
  };

  return (
    <VStack spacing={6} align="stretch" width="100%">
      {/* Header */}
      <VStack>
        <Flex w="100%" justify="space-between" alignItems="center">
          <TagByResourceType type="checkpoint_template" />
          <HStack>
            {data?.archivedAt && <ArchivedTag />}
            <PrimaryButton
              onClick={handleSubmit(onSubmit, () => {
                toast({
                  title: "Please fix the errors before continuing.",
                  status: "error",
                  duration: 5000,
                });
              })}
            >
              {id === "new" ? "Add" : "Save"}
            </PrimaryButton>
            {data && <TemplateResourceOptionMenu template={data} />}
          </HStack>
        </Flex>
        <NameField form={form} />
      </VStack>

      {/* Template attributes — When | Check For | Notify Via | Paths,
          framed top/bottom, vertical dividers between, side-by-side at the left.
          Mirrors the email/message/task/meeting template attribute strip. */}
      <HStack
        divider={<StackDivider borderColor="gray.200" />}
        spacing={0}
        py={4}
        borderTop="1px solid"
        borderBottom="1px solid"
        borderColor="gray.200"
        align="stretch"
        justify="flex-start"
      >
        <VStack flex="0 0 auto" spacing={2} align="stretch" px={6} minW="220px">
          <AttributeColumnLabel>When</AttributeColumnLabel>
          <FormControl variant="unstyled" isInvalid={!!get(form.formState.errors, "evaluateAtTrigger")}>
            <SchedulingTriggerModalButton
              form={form}
              label="When"
              fieldName="evaluateAtTrigger"
              nullLabel="No Timing Info"
              timingOnly
              allowWeekendsFieldName="allowWeekends"
            />
            <ErrorText error={get(form.formState.errors, "evaluateAtTrigger")} reserveErrorSpace={false} />
          </FormControl>
        </VStack>
        <VStack flex="0 0 auto" spacing={2} align="stretch" px={6} minW="200px">
          <AttributeColumnLabel>Check For</AttributeColumnLabel>
          <Flex>
            <Popover isOpen={isCheckTargetOpen} onOpen={onCheckTargetOpen} onClose={onCheckTargetClose}>
              <PopoverTrigger>
                <AttributeButtonWithRef bg="bg.control">
                  {CHECK_TARGET_OPTIONS.find((o) => o.value === checkTarget)?.label || checkTarget}
                </AttributeButtonWithRef>
              </PopoverTrigger>
              <PopoverContent
                pt="3"
                bg="var(--chakra-colors-chakra-body-bg)"
                minW={{ base: "100%", lg: "max-content" }}
                width="100%"
                maxWidth="350px"
              >
                <PopoverBody>
                  <GenericSelectable
                    value={checkTarget}
                    setValue={(v) => {
                      setValue("checkTarget", v as any, { shouldDirty: true });
                      onCheckTargetClose();
                    }}
                    options={CHECK_TARGET_OPTIONS}
                  />
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </Flex>
        </VStack>
        {/* Notify sentence — slot in the attributes row where "Notify Via"
            used to live. Reads as "Notify <recipients> via <method>". */}
        <VStack flex="0 0 auto" spacing={2} align="stretch" px={6} minW="220px">
          <AttributeColumnLabel>Notify</AttributeColumnLabel>
          <FormControl variant="unstyled" isInvalid={!!get(form.formState.errors, "recipients")}>
            <VStack spacing={2} align="stretch">
              {/* Recipient chips fill the column width; the "via <method>"
                  row stacks below so it reads as a continuation of the
                  sentence instead of fighting the chips for horizontal space. */}
              <Box>
                {deliveryMethod === "message" ? (
                  <UserOrChannelSelectField
                    name="recipients"
                    types={["users", "channels", "roles"]}
                    maxSelections={10}
                    channelExclusive
                    showPlusButton
                    placeholder="Select users, channels, or roles..."
                    variant="unstyled"
                    control={control}
                    onChangeCallback={onMessageRecipientsChange}
                  />
                ) : (
                  <Controller
                    control={control}
                    name="recipients"
                    render={({ field: { value: recipientValue, onChange: onRecipientsChange } }) => (
                      <UserRoleOrEmailMultiSelect
                        value={(recipientValue || []) as unknown as UserRoleOrEmailOption[]}
                        onChange={(newValue) => {
                          onRecipientsChange(newValue);
                          const names = (newValue || [])
                            .map((v: any) => {
                              const isRole = v?.recordType === "OnboardingJourneyNetworkRelation";
                              const name = v?.label || "";
                              return isRole ? `the ${name}` : name;
                            })
                            .filter(Boolean);
                          setRecipientNames(names);
                        }}
                        label="Add User, Role or Email"
                      />
                    )}
                  />
                )}
              </Box>
              <HStack spacing={2} align="center">
                <Text fontSize="sm" color="content.tertiary" fontWeight="medium">via</Text>
                <Popover isOpen={isDeliveryMethodOpen} onOpen={onDeliveryMethodOpen} onClose={onDeliveryMethodClose}>
                  <PopoverTrigger>
                    {/* Border + chevron make this read as a button at rest;
                        without them the label looks like static text. */}
                    <AttributeButtonWithRef
                      aria-label="Delivery method"
                      borderWidth="1px"
                      borderColor="border.primary"
                      borderRadius="md"
                      bg="bg.surface"
                      rightIcon={<Icon as={TbChevronDown} boxSize="14px" />}
                    >
                      {DELIVERY_METHOD_OPTIONS.find((o) => o.value === deliveryMethod)?.label || deliveryMethod}
                    </AttributeButtonWithRef>
                  </PopoverTrigger>
                  <PopoverContent
                    pt="3"
                    bg="var(--chakra-colors-chakra-body-bg)"
                    minW={{ base: "100%", lg: "max-content" }}
                    width="100%"
                    maxWidth="350px"
                  >
                    <PopoverBody>
                      <GenericSelectable
                        value={deliveryMethod}
                        setValue={(v) => {
                          setValue("deliveryMethod", v as any, { shouldDirty: true });
                          setValue("recipients", [], { shouldDirty: true });
                          setRecipientNames([]);
                          setHasChannelRecipient(false);
                          onDeliveryMethodClose();
                        }}
                        options={DELIVERY_METHOD_OPTIONS}
                      />
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </HStack>
            </VStack>
            <ErrorText error={get(form.formState.errors, "recipients")} reserveErrorSpace={false} />
          </FormControl>
        </VStack>
        <VStack flex="0 0 auto" spacing={2} align="stretch" px={6} minW="220px">
          <AttributeColumnLabel>Paths</AttributeColumnLabel>
          <Flex gap="1" align="center" wrap="wrap">
            {selectedPathsComponents}
            <SelectPathsMenuButton selectedPathIds={selectedPathIds} addPathId={addPathId} />
          </Flex>
        </VStack>
      </HStack>

      {/* How It Works */}
      <CheckpointPreview
        checkTarget={checkTarget}
        deliveryMethod={deliveryMethod}
        evaluateAtTrigger={evaluateAtTrigger}
        recipientNames={recipientNames}
        hasChannelRecipient={hasChannelRecipient}
      />

    </VStack>
  );
}

const OUTCOMES = [
  {
    icon: TbChecks,
    label: "Good to Go!",
    desc: "All tasks are complete, or no open tasks were found. No action needed.",
    borderColor: "green.400",
    color: "green",
  },
  {
    icon: TbAlertTriangle,
    label: "Needs a Nudge",
    desc: "Some tasks are still in progress. Please follow up to ensure they are completed.",
    borderColor: "orange.400",
    color: "orange",
  },
  {
    icon: TbCircleX,
    label: "Behind Schedule",
    desc: "There are overdue tasks that need attention. Please follow up as soon as possible.",
    borderColor: "red.400",
    color: "red",
  },
];

function CheckpointPreview({
  checkTarget,
  deliveryMethod,
  evaluateAtTrigger,
  recipientNames,
  hasChannelRecipient,
}: {
  checkTarget: string;
  deliveryMethod: string;
  evaluateAtTrigger: any;
  recipientNames: string[];
  hasChannelRecipient: boolean;
}) {
  const checkTargetLabel = CHECK_TARGET_OPTIONS.find((o) => o.value === checkTarget)?.label || checkTarget;

  let timingText = "at the scheduled time";
  if (evaluateAtTrigger?.data?.referenceEvent) {
    const summary = generateSchedulingSummary({ field: "evaluateAtTrigger", trigger: evaluateAtTrigger });
    if (summary) timingText = summary.toLowerCase();
  }

  const recipientText = recipientNames.length > 0 ? recipientNames.join(" & ") : "the selected recipients";

  const isMultiple = recipientNames.length > 1;
  let notifyPrefix: string;
  if (deliveryMethod === "email") {
    notifyPrefix = isMultiple ? "one email" : "an email";
  } else {
    notifyPrefix = isMultiple && !hasChannelRecipient ? "one message" : "a message";
  }

  return (
    <VStack align="stretch" spacing={2} maxW="600px">
      <Heading16>How It Works</Heading16>

      <Box bg="gray.50" borderRadius="md" p={4} border="0.5px solid" borderColor="gray.200">
        <Text
          fontSize="xs"
          fontWeight="600"
          color="content.tertiary"
          textTransform="uppercase"
          letterSpacing="wide"
          mb={2}
        >
          Checkpoint
        </Text>
        <Text fontSize="sm" color="content.primary" lineHeight="tall">
          <Text as="span" fontWeight="600">
            {timingText.charAt(0).toUpperCase() + timingText.slice(1)}
          </Text>
          , Camino will check the{" "}
          <Text as="span" fontWeight="600">
            {checkTargetLabel}
          </Text>{" "}
          to see which ones are open, overdue, and completed.
        </Text>
      </Box>

      <VStack spacing={0} align="center" my={-1}>
        <Box w="1px" h="6px" bg="gray.300" />
        <Text
          fontSize="2xs"
          fontWeight="600"
          color="content.tertiary"
          textTransform="uppercase"
          letterSpacing="wider"
          py={0.5}
        >
          and then
        </Text>
        <Box w="1px" h="6px" bg="gray.300" />
      </VStack>

      <Box bg="gray.50" borderRadius="md" p={4} border="0.5px solid" borderColor="gray.200">
        <Text
          fontSize="xs"
          fontWeight="600"
          color="content.tertiary"
          textTransform="uppercase"
          letterSpacing="wide"
          mb={2}
        >
          Notification
        </Text>
        <Text fontSize="sm" color="content.primary" lineHeight="tall" mb={4}>
          Send{" "}
          <Text as="span" fontWeight="600">
            {notifyPrefix}
          </Text>{" "}
          to{" "}
          <Text as="span" fontWeight="600">
            {recipientText}
          </Text>{" "}
          with
        </Text>

        <Text
          fontSize="xs"
          fontWeight="600"
          color="content.tertiary"
          textTransform="uppercase"
          letterSpacing="wide"
          mb={2}
        >
          Possible outcomes
        </Text>
        <VStack align="stretch" spacing={2}>
          {OUTCOMES.map(({ icon, label, desc, borderColor, color }) => (
            <HStack
              key={label}
              borderLeft="3px solid"
              borderLeftColor={borderColor}
              borderRadius="md"
              px={3}
              py={2.5}
              spacing={3}
              bg={`${color}.50`}
              border="1px solid"
              borderColor={`${color}.200`}
              opacity={color === "gray" ? 0.7 : 1}
            >
              <Icon as={icon} color={`${color}.500`} boxSize={4} flexShrink={0} />
              <Box>
                <Text fontSize="sm" fontWeight="600" color={`${color}.700`}>
                  {label}
                </Text>
                <Text fontSize="xs" color={`${color}.600`}>
                  {desc}
                </Text>
              </Box>
            </HStack>
          ))}
        </VStack>
      </Box>
    </VStack>
  );
}
